<template>
  <div class="admin-page">
    <div class="admin-header">
      <h1>Pointe Cohorts</h1>
      <router-link to="/pointe/admin/cohorts/new" class="btn-primary">+ New Cohort</router-link>
    </div>

    <div class="admin-nav">
      <router-link to="/pointe/admin/applications">Applications</router-link>
      <router-link to="/pointe/admin/consultations">Consultations</router-link>
      <router-link to="/pointe/admin/criteria">Criteria</router-link>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <div v-else-if="cohorts.length === 0" class="empty-state">
      <p>No cohorts yet. <router-link to="/pointe/admin/cohorts/new">Create your first cohort.</router-link></p>
    </div>

    <div v-else class="cohorts-table">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Open Date</th>
            <th>Close Date</th>
            <th>App Fee</th>
            <th>Cohort Fee</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cohort in cohorts" :key="cohort.id">
            <td class="cohort-name">{{ cohort.name }}</td>
            <td><span class="status-badge" :class="cohort.status">{{ cohort.status }}</span></td>
            <td>{{ formatDate(cohort.openDate) }}</td>
            <td>{{ formatDate(cohort.closeDate) }}</td>
            <td>${{ cohort.applicationFee }}</td>
            <td>${{ cohort.cohortFee }}</td>
            <td class="actions-cell">
              <router-link :to="'/pointe/admin/cohorts/edit/' + cohort.id" class="btn-edit">Edit</router-link>
              <button
                v-if="cohort.status === 'upcoming'"
                class="btn-action"
                @click="setStatus(cohort, 'open')"
              >Open</button>
              <button
                v-if="cohort.status === 'open'"
                class="btn-action"
                @click="setStatus(cohort, 'closed')"
              >Close</button>
              <button
                v-if="cohort.status === 'closed'"
                class="btn-action"
                @click="setStatus(cohort, 'archived')"
              >Archive</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      cohorts: [],
      loading: true
    }
  },
  async created () {
    await this.loadCohorts()
  },
  methods: {
    async loadCohorts () {
      this.loading = true
      const snap = await db.collection('pointe-cohorts').orderBy('createdAt', 'desc').get()
      this.cohorts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      this.loading = false
    },
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    async setStatus (cohort, status) {
      await db.collection('pointe-cohorts').doc(cohort.id).update({ status })
      cohort.status = status
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0;
}

.admin-nav {
  display: flex;
  gap: 20px;
  margin-bottom: 28px;
  border-bottom: 1px solid #e8e5e1;
  padding-bottom: 12px;
}

.admin-nav a {
  font-size: 14px;
  color: #666;
  text-decoration: none;
}

.admin-nav a:hover { color: #1a1a1a; }

.loading-wrap { padding: 40px; text-align: center; }
.empty-state { padding: 40px; text-align: center; color: #888; }

.cohorts-table { overflow-x: auto; }

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

th {
  text-align: left;
  padding: 10px 14px;
  border-bottom: 2px solid #e8e5e1;
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #888;
}

td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0eeec;
  color: #1a1a1a;
}

.cohort-name { font-weight: 600; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.status-badge.upcoming { background: #f0f0f0; color: #666; }
.status-badge.open { background: #e8f5e9; color: #2e7d32; }
.status-badge.closed { background: #fff3e0; color: #e65100; }
.status-badge.archived { background: #f5f5f5; color: #9e9e9e; }

.actions-cell { display: flex; gap: 8px; align-items: center; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 10px 20px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
  display: inline-block;
}

.btn-primary:hover { background: #333; }

.btn-edit, .btn-action {
  background: none;
  border: 1px solid #e8e5e1;
  padding: 5px 12px;
  font-size: 13px;
  font-family: 'Fanwood Text', Georgia, serif;
  cursor: pointer;
  border-radius: 2px;
  color: #1a1a1a;
  text-decoration: none;
  display: inline-block;
}

.btn-edit:hover, .btn-action:hover { border-color: #1a1a1a; }
</style>
